import type { ProviderFundsApi } from '@/api/services/providerFunds';
import { corporateFundsApi, providersAssetsApi } from '@/api/services/providerFunds';

// A per-page config object driving the shared ProviderFundsPage layer. The two pages
// (Providers Assets, Corporate Funds) differ only by AccountType, which surfaces here
// as: the summary API, which widgets to show, and whether PA-only features exist
// (second summary grid, Period Locking tab, Process card, Lock row action, manual-tx
// lock awareness).

export interface ProviderFundsPermissions {
  page: string;
  externalReconciliation: string;
  addTransactions: string;
  manualBalanceProcessing?: string;
  periodLocking?: string;
  lock?: string;
  unlock?: string;
}

export interface ProviderFundsConfig {
  key: string; // 'pa' | 'cf' — prefix for tab keys / state
  title: string;
  breadcrumb: string[];
  api: ProviderFundsApi;
  perms: ProviderFundsPermissions;

  // Summary tab.
  summarySectionTitle: string; // heading above the Money grid
  hasInternalsGrid: boolean; // PA shows a second "Internals" grid
  moneyGridPermGated: boolean; // PA hides the Money grid entirely if no ExternalReconciliation
  hasLockRowAction: boolean;

  // Transactions tab.
  hasTransactionsExcel: boolean; // PA keeps the Extract button; CF sets withExcelBtn=false (legacy)

  // Transactions widgets.
  showFeeWidgets: boolean; // PA shows Deposit/Withdrawal fee widgets (6 vs 4)
  absReserves: boolean; // PA uses Math.abs(reservesCaptured); CF uses raw
  excessTooltip: boolean; // PA excess widget has a tooltip; CF does not
  lockAwareManualCard: boolean; // PA manual-tx card is lock-aware

  // Details tab.
  hasProcessCard: boolean;
  detailsTabTitle: string; // PA = "Provider Details", CF = "Provider Fees"

  // Period locking tab (PA-only).
  hasPeriodLocking: boolean;

  // Excel fallback file names.
  summaryExcelName: string;
  transactionsExcelName: string;
}

export const PROVIDERS_ASSETS_CONFIG: ProviderFundsConfig = {
  key: 'pa',
  title: 'Providers Assets',
  breadcrumb: ['Home', 'Providers Assets'],
  api: providersAssetsApi,
  perms: {
    page: 'User.SafeguardingReconciliation.ProvidersAssets',
    externalReconciliation: 'User.SafeguardingReconciliation.ExternalReconciliation',
    addTransactions: 'User.SafeguardingReconciliation.ProvidersAssets.AddTransactions',
    manualBalanceProcessing: 'User.SafeguardingReconciliation.ProvidersAssets.ManualBalanceProcessing',
    periodLocking: 'User.SafeguardingReconciliation.ProvidersAssets.PeriodLocking',
    lock: 'User.SafeguardingReconciliation.ProvidersAssets.PeriodLocking.Lock',
    unlock: 'User.SafeguardingReconciliation.ProvidersAssets.PeriodLocking.Unlock',
  },
  summarySectionTitle: 'Money Providers',
  hasInternalsGrid: true,
  moneyGridPermGated: true,
  hasLockRowAction: true,
  hasTransactionsExcel: true,
  showFeeWidgets: true,
  absReserves: true,
  excessTooltip: true,
  lockAwareManualCard: true,
  hasProcessCard: true,
  detailsTabTitle: 'Provider Details',
  hasPeriodLocking: true,
  summaryExcelName: 'Providers Summary.xlsx',
  transactionsExcelName: 'Provider Transactions.xlsx',
};

export const CORPORATE_FUNDS_CONFIG: ProviderFundsConfig = {
  key: 'cf',
  title: 'Corporate Funds',
  breadcrumb: ['Home', 'Corporate Funds'],
  api: corporateFundsApi,
  perms: {
    page: 'User.SafeguardingReconciliation.CorporateFunds',
    externalReconciliation: 'User.SafeguardingReconciliation.ExternalReconciliation',
    addTransactions: 'User.SafeguardingReconciliation.CorporateFunds.AddTransactions',
  },
  summarySectionTitle: 'Money Providers',
  hasInternalsGrid: false,
  moneyGridPermGated: false,
  hasLockRowAction: false,
  hasTransactionsExcel: false,
  showFeeWidgets: false,
  absReserves: false,
  excessTooltip: false,
  lockAwareManualCard: false,
  hasProcessCard: false,
  detailsTabTitle: 'Provider Fees',
  hasPeriodLocking: false,
  summaryExcelName: 'Corporate Funds Summary.xlsx',
  transactionsExcelName: 'Corporate Funds Transactions.xlsx',
};

// yesterday = today - 1 day (default + max date everywhere), as 'YYYY-MM-DD'.
export function yesterdayIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// today - n days, as 'YYYY-MM-DD'.
export function daysAgoIso(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
